import { apiGet, apiPost, apiPut, apiDelete, apiPostFormRaw } from './client';
import type {
  ImportTarget,
  ImportColumn,
  ImportDef,
  ImportDefSummary,
  ImportDefCreate,
  ImportRunResult,
  ImportRunIssue,
} from '../types/Import';

export const getImportTargets = () =>
  apiGet<ImportTarget[]>('/api/imports/targets');

export const getImportTargetSchema = (table: string) =>
  apiGet<ImportColumn[]>(
    `/api/imports/targets/${encodeURIComponent(table)}/schema`,
  );

export const getImportDefs = () =>
  apiGet<ImportDefSummary[]>('/api/imports/defs');

export const getImportDef = (id: number) =>
  apiGet<ImportDef>(`/api/imports/defs/${id}`);

export const createImportDef = (dto: ImportDefCreate) =>
  apiPost<ImportDefCreate, ImportDef>('/api/imports/defs', dto);

// Backend uses PUT for full update.
export const updateImportDef = (id: number, dto: ImportDefCreate) =>
  apiPut<ImportDefCreate, ImportDef>(`/api/imports/defs/${id}`, dto);

// Backend uses DELETE (204 No Content on success).
export const deleteImportDef = (id: number): Promise<void> =>
  apiDelete(`/api/imports/defs/${id}`);

export type RunOutcome =
  | { ok: true; result: ImportRunResult }
  | { ok: false; error: string; issues: ImportRunIssue[] };

export async function runImport(
  importDefId: number,
  existingMode: string,
  file: File,
  nullLiteralAsEmpty = false,
): Promise<RunOutcome> {
  const form = new FormData();
  form.append('importDefId', String(importDefId));
  form.append('existingMode', existingMode);
  form.append('nullLiteralAsEmpty', String(nullLiteralAsEmpty));
  form.append('file', file);
  const res = await apiPostFormRaw('/api/imports/run', form);
  if (res.ok) return { ok: true, result: (await res.json()) as ImportRunResult };
  // 400 returns { error, issues }
  try {
    const body = await res.json();
    return {
      ok: false,
      error: body.error ?? 'Import failed',
      issues: body.issues ?? [],
    };
  } catch {
    return { ok: false, error: `Import failed (${res.status})`, issues: [] };
  }
}
